import type { IsoDateStr } from '@moment/domain-types'

import {
  DEFAULT_TIME_ZONE,
  dayStartInstant,
  todayIsoDate as zonedTodayIsoDate,
} from '@moment/utils/datetime'

import { DateFormat, formatDate } from './dateFormatting'

// Regex patterns for date format validation
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/ // YYYY-MM-DD
const US_DATE_REGEX = /^\d{2}\/\d{2}\/\d{4}$/ // MM/DD/YYYY
const INTEGER_DATE_REGEX = /^\d{8}$/ // YYYYMMDD

/**
 * Today in market time. Order events are stamped
 * `toDate(timestamp, 'America/New_York')`, and horizon parses the `start`
 * cursor in that same zone, so the viewer's own day is the wrong day whenever
 * the two disagree.
 */
const marketToday = (): IsoDateStr => zonedTodayIsoDate(DEFAULT_TIME_ZONE)

/**
 * The instant the market day began, for comparing against event timestamps.
 *
 * Named for the zone because both meanings of "today" are live in this app: a
 * surface showing the viewer their own day wants
 * `dayStartInstant(todayIsoDate(viewerTimeZone()))` instead.
 */
export const getMarketDayStart = (): number =>
  dayStartInstant(marketToday(), DEFAULT_TIME_ZONE).epochMilliseconds

/** The first message number of the market day, as the events API expects it. */
export const getTodayStartHwm = (): string =>
  `${marketToday().replaceAll('-', '')}0000000000`

/**
 * The same watermark as a number, which loses precision above
 * `Number.MAX_SAFE_INTEGER` — watermarks travel as strings everywhere else.
 */
export const getTodayBaseHwm = (): number => Number(getTodayStartHwm())

// Today as a UTC YYYY-MM-DD date string. Matches how SORM account-setting reads
// compare `effective_date <= today()` (ClickHouse today() is UTC).
export const todayIsoDate = (): string => new Date().toISOString().slice(0, 10)

// Today as YYYY-MM-DD in the viewer's LOCAL timezone — unlike `todayIsoDate`
// (UTC), which flips date-derived flags at the wrong moment for users west of
// UTC. Lex comparison against other `YYYY-MM-DD` strings is safe because the
// format is calendar-sortable.
export const localTodayIso = (): string =>
  formatDate(new Date(), DateFormat.ISO_DATE)

/**
 * Parses a YYYY-MM-DD calendar string into a local-midnight Date (avoids the
 * UTC-midnight drift of `new Date(iso)` in negative-offset zones).
 * @returns the parsed Date, or `null` for nullish input, wrong shape, or an
 * impossible calendar date (Feb 30, month 13, non-leap Feb 29).
 */
export const parseCalendarDate = (
  value: string | null | undefined,
): Date | null => {
  if (value == null) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (match == null) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day)
  // `new Date` rolls impossible dates (Feb 30 → Mar 2); a non-round-trip
  // means the input wasn't a real calendar date.
  const roundTrips =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  return roundTrips ? date : null
}

/**
 * Narrows a date-or-datetime string to its `YYYY-MM-DD` prefix. SORM
 * serializes `type: date` columns as UTC-midnight instants
 * (`2026-07-02T00:00:00Z`), which fails `IsoDateStr` validation and breaks lex
 * comparison against calendar dates. Deliberately textual, not a timezone
 * conversion — converting UTC midnight to a local instant would shift the day
 * backward west of UTC.
 * @returns the calendar prefix, `undefined` for nullish input, or the input
 * unchanged when it doesn't start with a `YYYY-MM-DD` date.
 */
export const toCalendarDateStr = (
  value: string | null | undefined,
): string | undefined => {
  if (value == null) return undefined
  const match = /^(\d{4}-\d{2}-\d{2})(?:[T\s].*)?$/.exec(value)
  return match?.[1] ?? value
}

/**
 * Converts a date string from MM/DD/YYYY format to ISO format with optional separator
 * @param dateStr - Date string in MM/DD/YYYY format
 * @param separator - Optional separator for the output format (default: '-')
 * @returns Date string in YYYY[separator]MM[separator]DD format (ex: 2025-08-14 or 20250814)
 *
 * @example
 * formatUSDateToISO('8/14/2025') // Returns '2025-08-14'
 * formatUSDateToISO('12/25/2025') // Returns '2025-12-25'
 * formatUSDateToISO('03/27/2024', '') // Returns '20240327'
 * formatUSDateToISO('01/05/2024', '/') // Returns '2024/01/05'
 */
export const formatUSDateToISO = (
  dateStr: string,
  separator: string = '-',
): string => {
  const [month, day, year] = dateStr.split('/')
  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  const formattedMonth = month!.padStart(2, '0')
  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  const formattedDay = day!.padStart(2, '0')
  return separator
    ? `${year}${separator}${formattedMonth}${separator}${formattedDay}`
    : `${year}${formattedMonth}${formattedDay}`
}

/**
 * Converts a date string from MM/DD/YYYY format to YYYYMMDD integer format
 * @param dateStr - Date string in MM/DD/YYYY format
 * @returns Integer in YYYYMMDD format
 *
 * @example
 * formatUSDateToInteger('03/27/2024') // Returns 20240327
 * formatUSDateToInteger('01/05/2024') // Returns 20240105
 * formatUSDateToInteger('12/31/2024') // Returns 20241231
 */
export const formatUSDateToInteger = (dateStr: string): number => {
  return parseInt(formatUSDateToISO(dateStr, ''), 10)
}

/**
 * Converts date from YYYYMMDD format to MM/DD/YYYY format
 * @param date - Date in YYYYMMDD format (e.g., 20250930)
 * @returns Formatted date in MM/DD/YYYY format (e.g., "09/30/2025"), or null if input is invalid
 */
export const formatIntegerToUSDate = (
  date: number | null | undefined,
): string | null => {
  if (!date) return null

  const dateStr = date.toString()
  if (!INTEGER_DATE_REGEX.test(dateStr)) return null

  // YYYYMMDD format indices
  const year = dateStr.substring(0, 4)
  const month = dateStr.substring(4, 6)
  const day = dateStr.substring(6, 8)

  return `${month}/${day}/${year}`
}

/**
 * Converts ISO date format to US date format
 * @param isoDate - Date string in YYYY-MM-DD format
 * @returns Date string in MM/DD/YYYY format, or empty string if invalid
 */
export const isoToUSDate = (isoDate: string): string => {
  if (!isoDate || !ISO_DATE_REGEX.test(isoDate)) return ''
  const [year, month, day] = isoDate.split('-')
  return `${month}/${day}/${year}`
}

/**
 * Converts US date format to ISO date format
 * @param usDate - Date string in MM/DD/YYYY format
 * @returns Date string in YYYY-MM-DD format, or empty string if invalid
 */
export const usToISODate = (usDate: string): string => {
  if (!usDate || !US_DATE_REGEX.test(usDate)) return ''
  const [month, day, year] = usDate.split('/')
  return `${year}-${month}-${day}`
}

/**
 * Converts ISO date string to integer format
 * @param isoDate - Date string in YYYY-MM-DD format
 * @returns Integer in YYYYMMDD format, or null if invalid
 *
 * @example
 * isoDateToInt('2025-01-15') // Returns 20250115
 * isoDateToInt('2025-12-31') // Returns 20251231
 * isoDateToInt('invalid') // Returns null
 */
export const isoDateToInt = (isoDate: string): number | null => {
  if (!isoDate || !ISO_DATE_REGEX.test(isoDate)) return null
  const dateInt = parseInt(isoDate.replace(/-/g, ''), 10)
  // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
  return isNaN(dateInt) ? null : dateInt
}

/**
 * Converts integer date format to ISO date string
 * @param date - Date in YYYYMMDD format (e.g., 20250115)
 * @returns Date string in YYYY-MM-DD format, or null if invalid
 *
 * @example
 * intToIsoDate(20250115) // Returns '2025-01-15'
 * intToIsoDate(20251231) // Returns '2025-12-31'
 * intToIsoDate(null) // Returns null
 */
export const intToIsoDate = (
  date: number | null | undefined,
): string | null => {
  if (!date) return null
  const dateStr = date.toString()
  if (!INTEGER_DATE_REGEX.test(dateStr)) return null
  const year = dateStr.substring(0, 4)
  const month = dateStr.substring(4, 6)
  const day = dateStr.substring(6, 8)
  return `${year}-${month}-${day}`
}

/**
 * Converts US date string to integer format
 * @param usDate - Date string in MM/DD/YYYY format
 * @returns Integer in YYYYMMDD format, or null if invalid
 *
 * @example
 * usDateToInt('01/15/2025') // Returns 20250115
 * usDateToInt('12/31/2024') // Returns 20241231
 * usDateToInt('invalid') // Returns null
 */
export const usDateToInt = (usDate: string): number | null => {
  if (!usDate || !US_DATE_REGEX.test(usDate)) return null
  const [month, day, year] = usDate.split('/')
  const dateInt = parseInt(`${year}${month}${day}`, 10)
  return Number.isNaN(dateInt) ? null : dateInt
}

/**
 * Checks that a string is both shape-valid (YYYY-MM-DD) and a real calendar
 * date.
 * @param value - Date string expected to match YYYY-MM-DD
 * @returns `true` if `value` matches YYYY-MM-DD and represents a real calendar
 * date; `false` otherwise (wrong shape, or impossible date like 2026-02-30,
 * month 13, or non-leap Feb 29).
 *
 * @example
 * isValidYmd('2026-05-14') // Returns true
 * isValidYmd('2024-02-29') // Returns true (leap year)
 * isValidYmd('2026-02-30') // Returns false (Feb 30 doesn't exist)
 * isValidYmd('2026-13-01') // Returns false (month 13)
 * isValidYmd('2026-5-14')  // Returns false (single-digit month)
 * isValidYmd('hello')      // Returns false
 */
export const isValidYmd = (value: string): boolean =>
  parseCalendarDate(value) !== null
